import { Component, inject, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { IdentityVerificationService } from '../../services/identity-verification/identity-verification.service';
import { ToastService } from '../../services/general/toast.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-photo-capture',
  imports: [
    CommonModule,
  ],
  templateUrl: './photo-capture.component.html',
  styleUrl: './photo-capture.component.scss'
})
export class PhotoCaptureComponent implements OnInit, OnDestroy {
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _identityService = inject(IdentityVerificationService);
  private _toast = inject(ToastService);

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

  identityNumber: string = '';
  stream: MediaStream | null = null;
  isCapturing: boolean = false;
  capturedImage: string | null = null;
  errorMessage: string = '';
  isSubmitting: boolean = false;
  isVerified: boolean = false;

  ngOnInit() {
    // Get identity number from localStorage
    if (typeof window !== 'undefined') {
      this.identityNumber = localStorage.getItem('identityNumber') || '';
    }
    
    // Get id from query params (if needed for future API calls)
    this._route.queryParams.subscribe(params => {
      const id = params['id'] || '';
      // Store id if needed for API calls
      if (id && typeof window !== 'undefined') {
        localStorage.setItem('verificationId', id);
      }
    });
  }

  async startCamera() {
    try {
      this.errorMessage = '';
      this.isCapturing = true;

      // Request camera access at higher resolution for better quality
      // We'll downscale to 640x480 later with high quality rendering
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 960 },
          facingMode: 'user' // Front-facing camera
        }
      });

      if (this.videoElement?.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.stream;
        this.videoElement.nativeElement.play();
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      this.errorMessage = 'Unable to access camera. Please ensure you have granted camera permissions.';
      this.isCapturing = false;
    }
  }

  capturePhoto() {
    if (!this.videoElement?.nativeElement || !this.canvasElement?.nativeElement) {
      return;
    }

    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    // Set canvas dimensions to 640x480
    canvas.width = 640;
    canvas.height = 480;

    // Configure context for maximum image quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw video frame to canvas with high quality downscaling
    // This preserves more detail when downscaling from higher resolution
    ctx.drawImage(video, 0, 0, 640, 480);

    // Stop camera stream immediately
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
      this.videoElement.nativeElement.pause();
    }
    
    this.isCapturing = false;

    // Automatically crop and convert to PNG format
    this.processImageToPNG(canvas).then((dataUrl) => {
      this.capturedImage = dataUrl;
    }).catch((error) => {
      console.error('Error processing image:', error);
      this.errorMessage = 'Failed to process image';
    });
  }

  async processImageToPNG(canvas: HTMLCanvasElement): Promise<string> {
    return new Promise((resolve, reject) => {
      // Ensure dimensions are exactly 640x480
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = 640;
      outputCanvas.height = 480;
      const ctx = outputCanvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Configure context for high quality rendering to preserve liveness features
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the captured image to the output canvas (cropped to 640x480)
      ctx.drawImage(canvas, 0, 0, 640, 480);

      // Convert to PNG and compress to under 1MB
      this.compressPNG(outputCanvas).then((dataUrl) => {
        resolve(dataUrl);
      }).catch((error) => {
        reject(error);
      });
    });
  }

  async compressPNG(canvas: HTMLCanvasElement): Promise<string> {
    return new Promise((resolve, reject) => {
      // Target ~950KB to preserve maximum quality for liveness detection
      // Avoid JPEG compression to prevent artifacts that make images look processed
      const targetSizeBytes = 950 * 1024; // 950KB
      
      // Try PNG directly first - avoid JPEG compression to preserve natural appearance
      canvas.toBlob(
        (pngBlob) => {
          if (!pngBlob) {
            reject(new Error('Failed to create PNG blob'));
            return;
          }

          // If already under target size, use it directly (best quality)
          if (pngBlob.size <= targetSizeBytes) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result as string);
            };
            reader.onerror = () => {
              reject(new Error('Failed to read PNG'));
            };
            reader.readAsDataURL(pngBlob);
            return;
          }

          // If over target size, try reducing dimensions slightly while maintaining 640x480 aspect ratio
          // This preserves quality better than JPEG compression
          let width = 640;
          let height = 480;
          let attempts = 0;
          const maxAttempts = 8;

          const tryReduceDimensions = () => {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');
            
            if (!tempCtx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }

            // Use maximum quality rendering
            tempCtx.imageSmoothingEnabled = true;
            tempCtx.imageSmoothingQuality = 'high';

            // Draw original canvas scaled down with high quality
            tempCtx.drawImage(canvas, 0, 0, width, height);
            
            tempCanvas.toBlob(
              (reducedPngBlob) => {
                if (!reducedPngBlob) {
                  reject(new Error('Failed to create reduced PNG'));
                  return;
                }

                // If under target size or max attempts reached, use it
                if (reducedPngBlob.size <= targetSizeBytes || attempts >= maxAttempts) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    resolve(reader.result as string);
                  };
                  reader.onerror = () => {
                    reject(new Error('Failed to read reduced PNG'));
                  };
                  reader.readAsDataURL(reducedPngBlob);
                } else {
                  // Reduce dimensions by 2% and try again (very small reduction)
                  width = Math.floor(width * 0.98);
                  height = Math.floor(height * 0.98);
                  attempts++;
                  tryReduceDimensions();
                }
              },
              'image/png'
            );
          };

          tryReduceDimensions();
        },
        'image/png'
      );
    });
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.isCapturing = false;
    
    // Clear video element source to hide the black box
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
      this.videoElement.nativeElement.pause();
    }
  }

  retakePhoto() {
    this.capturedImage = null;
    this.errorMessage = '';
    this.isVerified = false; // Reset verification status when retaking
    // Small delay to ensure UI updates before starting camera
    setTimeout(() => {
      this.startCamera();
    }, 100);
  }

  submitPhoto() {
    if (!this.capturedImage) {
      this.errorMessage = 'No photo to submit';
      return;
    }

    // Get id from localStorage
    const id = typeof window !== 'undefined' ? localStorage.getItem('verificationId') : null;
    if (!id) {
      this.errorMessage = 'Verification ID not found. Please start over.';
      this._toast.error('Verification ID not found. Please start over.');
      return;
    }

    // Convert data URL to blob
    const blob = this.dataURLtoBlob(this.capturedImage);
    
    // Check if file is too large (e.g., > 1.5MB)
    const fileSizeInMB = blob.size / (1024 * 1024);
    if (fileSizeInMB > 1.5) {
      this.errorMessage = 'Image is too large. Please retake the photo.';
      this._toast.error('Image is too large. Please retake the photo.');
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append('image', blob, 'photo.png');
    formData.append('imageType', 'PNG');
    formData.append('id', id);

    this.isSubmitting = true;
    this.errorMessage = '';

    this._identityService.verifyIdentity(formData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        
        if (response.isVerified === true) {
          // Show verification success
          this.isVerified = true;
          this._toast.success('Verification successful!');
          
          // Clear verification data from localStorage
          if (typeof window !== 'undefined') {
            localStorage.removeItem('verificationId');
            localStorage.removeItem('identityNumber');
          }
        } else {
          // Display the message from response
          const message = response.message || 'Verification failed. Please try again.';
          this.errorMessage = message;
          this._toast.error(message);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        
        if (error.status === 404 || error.status === 500) {
          const errorMessage = error.error?.message || 'An error occurred. Please try again.';
          this.errorMessage = errorMessage;
          this._toast.error(errorMessage);
        } else {
          this.errorMessage = 'An error occurred. Please try again.';
          this._toast.error('An error occurred. Please try again.');
        }
      }
    });
  }

  // Helper method to convert data URL to blob for API submission
  private dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1] || arr[0]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  checkLighting(): boolean {
    // Basic lighting check - in a real app, you might use more sophisticated methods
    if (!this.videoElement?.nativeElement) {
      return false;
    }

    const video = this.videoElement.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return false;
    }

    ctx.drawImage(video, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Calculate average brightness
    let brightness = 0;
    for (let i = 0; i < data.length; i += 4) {
      brightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
    }
    brightness = brightness / (data.length / 4);

    // Consider sufficient lighting if brightness is above 100 (out of 255)
    return brightness > 100;
  }

  async captureWithLightingCheck() {
    if (!this.checkLighting()) {
      this.errorMessage = 'Insufficient lighting detected. Please ensure you are in a well-lit area.';
      return;
    }

    this.capturePhoto();
  }

  ngOnDestroy() {
    this.stopCamera();
  }
}
